import { FC, useEffect } from 'react';
import { Grid, Typography, Divider } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useUsers } from '../../contexts/users.context';
import { GuestUser, GuestType, UserStatus } from '../../users.interfaces';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { zDateStringFromInput, zOptionalDateStringFromInput } from 'src/utils/zodFormHelpers';
import { useCreateGuestUser } from '../../hooks/useGuestHooks';

type GuestFormData = {
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  email: string;
  phone: string;
  userName: string;
  userPassword: string;
  guestType: GuestType;
  organization?: string;
  designation?: string;
  eventId?: string;
  eventName?: string;
  invitedBy: string;
  invitationDate: string;
  accessLevel: 'limited' | 'standard' | 'full';
  status: UserStatus;
  notes?: string;
  address: {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
  };
};

const getGuestFormSchema = (translateText: (key: string) => string) =>
  z.object({
    firstName: z
      .string()
      .min(2, translateText('common.fieldRequired'))
      .max(50, 'First name must not exceed 50 characters'),
    lastName: z
      .string()
      .min(2, translateText('common.fieldRequired'))
      .max(50, 'Last name must not exceed 50 characters'),
    email: z
      .string()
      .email('Please enter a valid email address')
      .min(1, translateText('common.fieldRequired')),
    phone: z
      .string()
      .min(10, 'Phone number must be at least 10 digits')
      .max(15, 'Phone number must not exceed 15 digits'),
    userName: z.string().min(3, 'Username must be at least 3 characters'),
    userPassword: z.string().min(6, 'Password must be at least 6 characters'),
    guestType: z.nativeEnum(GuestType, {
      required_error: translateText('common.fieldRequired'),
    }),
    organization: z.string().optional(),
    designation: z.string().optional(),
    eventId: z.string().optional(),
    eventName: z.string().optional(),
    invitedBy: z.string().min(1, translateText('common.fieldRequired')),
    invitationDate: zDateStringFromInput({
      requiredMessage: translateText('common.fieldRequired') || undefined,
      message: translateText('common.dateInvalid') || undefined,
    }),
    accessLevel: z.enum(['limited', 'standard', 'full']),
    status: z.nativeEnum(UserStatus),
    dateOfBirth: zOptionalDateStringFromInput({ message: 'Please enter a valid date (YYYY-MM-DD)' }),
    notes: z.string().optional(),
    address: z.object({
      street1: z.string().min(1, translateText('common.fieldRequired')),
      street2: z.string().optional(),
      city: z.string().min(1, translateText('common.fieldRequired')),
      state: z.string().min(1, translateText('common.fieldRequired')),
      pinCode: z.string().min(1, translateText('common.fieldRequired')),
      country: z.string().min(1, translateText('common.fieldRequired')),
    }),
  });

const getGuestTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: GuestType.VENDOR,
      value: translateText('users.options.guestType.vendor'),
    },
    {
      key: GuestType.CONTRACTOR,
      value: translateText('users.options.guestType.contractor'),
    },
    {
      key: GuestType.SPEAKER,
      value: translateText('users.options.guestType.speaker'),
    },
    {
      key: GuestType.VOLUNTEER,
      value: translateText('users.options.guestType.volunteer'),
    },
    {
      key: GuestType.OFFICIAL,
      value: translateText('users.options.guestType.official'),
    },
    {
      key: GuestType.OTHER,
      value: translateText('users.options.guestType.other'),
    },
  ];

const getStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: UserStatus.ACTIVE,
      value: translateText('users.options.status.active'),
    },
    {
      key: UserStatus.INACTIVE,
      value: translateText('users.options.status.inactive'),
    },
    {
      key: UserStatus.PENDING,
      value: translateText('users.options.status.pending'),
    },
    {
      key: UserStatus.BLOCKED,
      value: translateText('users.options.status.blocked'),
    },
  ];

const getAccessLevelOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: 'limited',
      value: translateText('users.forms.fields.accessLevel.limited'),
    },
    {
      key: 'standard',
      value: translateText('users.forms.fields.accessLevel.standard'),
    },
    { key: 'full', value: translateText('users.forms.fields.accessLevel.full') },
  ];

const AddEditGuestUserView: FC = () => {
  const { translateText } = useTranslation();
  const { state, closeAddEditGuest } = useUsers();
  const {
    isAddEditGuestOpen,
    isCreateMode,
    isEditMode,
    selectedUser,
    isLoading,
  } = state;

  const createMutation = useCreateGuestUser();

  const guestFormSchema = getGuestFormSchema(translateText);
  const guestTypeOptions = getGuestTypeOptions(translateText);
  const statusOptions = getStatusOptions(translateText);
  const accessLevelOptions = getAccessLevelOptions(translateText);

  const defaultValues: GuestFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    userName: '',
    userPassword: '',
    dateOfBirth: '',
    guestType: GuestType.OTHER,
    organization: '',
    designation: '',
    eventId: '',
    eventName: '',
    invitedBy: '',
    invitationDate: '',
    accessLevel: 'limited',
    status: UserStatus.PENDING,
    notes: '',
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      pinCode: '',
      country: '',
    },
  };

  const methods = useForm<GuestFormData>({
    resolver: zodResolver(guestFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;

  useEffect(() => {
    if (isAddEditGuestOpen && selectedUser && isEditMode) {
      const guestUser = selectedUser as GuestUser;
      reset({
        firstName: guestUser.firstName || '',
        lastName: guestUser.lastName || '',
        email: guestUser.email || '',
        phone: guestUser.phone || '',
        userName: '',
        userPassword: '',
        dateOfBirth: (guestUser as any).dateOfBirth || '',
        guestType: guestUser.guestType || GuestType.OTHER,
        organization: guestUser.organization || '',
        designation: guestUser.designation || '',
        eventId: guestUser.eventId || '',
        eventName: guestUser.eventName || '',
        invitedBy: guestUser.invitedBy || '',
        invitationDate: guestUser.invitationDate || '',
        accessLevel: guestUser.accessLevel || 'limited',
        status: guestUser.status || UserStatus.PENDING,
        notes: guestUser.notes || '',
        address: { street1: '', city: '', state: '', pinCode: '', country: '' },
      });
    } else if (isAddEditGuestOpen && isCreateMode) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddEditGuestOpen, selectedUser, isEditMode, isCreateMode, reset]);

  const onSubmitForm = async (data: GuestFormData) => {
    if (!isCreateMode) {
      // TODO: Implement update
      closeAddEditGuest();
      return;
    }

    try {
      await createMutation.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        userName: data.userName,
        userPassword: data.userPassword,
        dateOfBirth: data.dateOfBirth,
        guestType: data.guestType,
        organization: data.organization,
        designation: data.designation,
        eventId: data.eventId,
        eventName: data.eventName,
        invitedBy: data.invitedBy,
        invitationDate: data.invitationDate,
        accessLevel: data.accessLevel as any,
        status: data.status,
        address: data.address,
        userCategory: 'external',
      });
      closeAddEditGuest();
    } catch (error) {
      console.error('Error creating guest user:', error);
    }
  };

  const title = isEditMode
    ? translateText('users.forms.editGuest') || 'Edit Guest User'
    : translateText('users.forms.addGuest') || 'Add Guest User';

  return (
    <StructuredModal
      isOpen={isAddEditGuestOpen}
      onClose={closeAddEditGuest}
      title={title}
      drawerWidth="60vw"
      footerPositiveAction={handleSubmit(onSubmitForm)}
      footerPositiveActionText={
        isEditMode
          ? translateText('common.update')
          : translateText('common.save')
      }
      footerNegativeAction={closeAddEditGuest}
      footerNegativeActionText={translateText('common.cancel')}
      isLoading={isLoading || createMutation.isPending}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmitForm)}
          style={{ width: '100%' }}
          data-testid="guest-user-form"
        >
          <Grid
            container
            spacing={{ xs: 2, sm: 3 }}
            sx={{ p: { xs: 1, sm: 2 } }}
            data-testid="guest-form-container"
          >
            <Grid item xs={12} data-testid="basic-details-section">
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
              >
                {translateText('users.forms.basicInformation') ||
                  'Basic Information'}
              </Typography>
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="first-name-field">
              <ControlledTextField
                control={control}
                name="firstName"
                id="first-name"
                label={
                  translateText('users.forms.fields.firstName') || 'First Name'
                }
                errorMessage={errors.firstName?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="last-name-field">
              <ControlledTextField
                control={control}
                name="lastName"
                id="last-name"
                label={
                  translateText('users.forms.fields.lastName') || 'Last Name'
                }
                errorMessage={errors.lastName?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="email-field">
              <ControlledTextField
                control={control}
                name="email"
                id="email"
                label={translateText('auth.email') || 'Email'}
                errorMessage={errors.email?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="phone-field">
              <ControlledTextField
                control={control}
                name="phone"
                id="phone"
                label={translateText('users.forms.fields.phone') || 'Phone'}
                errorMessage={errors.phone?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="dob-field">
              <ControlledTextField
                control={control}
                name="dateOfBirth"
                id="date-of-birth"
                label="Date of Birth"
                type="date"
                errorMessage={(errors as any).dateOfBirth?.message}
                isGridItem={false}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} data-testid="credentials-section">
              <Typography
                variant="h6"
                gutterBottom
                sx={{ mt: { xs: 1.5, sm: 2 }, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
              >
                {translateText('users.forms.loginCredentials') || 'Login Credentials'}
              </Typography>
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="username-field">
              <ControlledTextField
                control={control}
                name="userName"
                id="user-name"
                label={translateText('users.forms.fields.userName') || 'Username'}
                errorMessage={errors.userName?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="password-field">
              <ControlledTextField
                control={control}
                name="userPassword"
                id="user-password"
                label={translateText('users.forms.fields.userPassword') || 'Password'}
                type="password"
                errorMessage={errors.userPassword?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="guest-type-field">
              <ControlledSelectField
                control={control}
                name="guestType"
                id="guest-type"
                label={translateText('users.filters.guestType') || 'Guest Type'}
                itemList={guestTypeOptions}
                errorMessage={errors.guestType?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="status-field">
              <ControlledSelectField
                control={control}
                name="status"
                id="status"
                label={translateText('common.status') || 'Status'}
                itemList={statusOptions}
                errorMessage={errors.status?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} data-testid="professional-details-section">
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  mt: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
                }}
              >
                {translateText('users.forms.professionalDetails') ||
                  'Professional Details'}
              </Typography>
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="organization-field">
              <ControlledTextField
                control={control}
                name="organization"
                id="organization"
                label={
                  translateText('users.forms.fields.organization') ||
                  'Organization'
                }
                errorMessage={errors.organization?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="designation-field">
              <ControlledTextField
                control={control}
                name="designation"
                id="designation"
                label={
                  translateText('users.forms.fields.designation') ||
                  'Designation'
                }
                errorMessage={errors.designation?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} data-testid="invitation-details-section">
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  mt: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
                }}
              >
                {translateText('users.forms.invitationDetails') ||
                  'Invitation Details'}
              </Typography>
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="invited-by-field">
              <ControlledTextField
                control={control}
                name="invitedBy"
                id="invited-by"
                label={
                  translateText('users.forms.fields.invitedBy') || 'Invited By'
                }
                errorMessage={errors.invitedBy?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="invitation-date-field">
              <ControlledTextField
                control={control}
                name="invitationDate"
                id="invitation-date"
                label={
                  translateText('users.forms.fields.invitationDate') ||
                  'Invitation Date'
                }
                type="date"
                errorMessage={errors.invitationDate?.message}
                required
                isGridItem={false}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="event-name-field">
              <ControlledTextField
                control={control}
                name="eventName"
                id="event-name"
                label={
                  translateText('users.forms.fields.eventName') || 'Event Name'
                }
                errorMessage={errors.eventName?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="access-level-field">
              <ControlledSelectField
                control={control}
                name="accessLevel"
                id="access-level"
                label={
                  translateText('users.forms.fields.accessLevel.label') ||
                  'Access Level'
                }
                itemList={accessLevelOptions}
                errorMessage={errors.accessLevel?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} data-testid="notes-field">
              <ControlledTextField
                control={control}
                name="notes"
                id="notes"
                label={translateText('common.remarks') || 'Notes'}
                errorMessage={errors.notes?.message}
                multiline
                rows={3}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} data-testid="address-section">
              <Typography
                variant="h6"
                gutterBottom
                sx={{ mt: { xs: 1.5, sm: 2 }, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
              >
                {translateText('users.forms.addressDetails') || 'Address'}
              </Typography>
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="address-street1-field">
              <ControlledTextField
                control={control}
                name="address.street1"
                id="address-street1"
                label={translateText('users.forms.fields.street') || 'Street'}
                errorMessage={errors.address?.street1?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="address-city-field">
              <ControlledTextField
                control={control}
                name="address.city"
                id="address-city"
                label={translateText('users.forms.fields.city') || 'City'}
                errorMessage={errors.address?.city?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={4} data-testid="address-state-field">
              <ControlledTextField
                control={control}
                name="address.state"
                id="address-state"
                label={translateText('users.forms.fields.state') || 'State'}
                errorMessage={errors.address?.state?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={4} data-testid="address-pincode-field">
              <ControlledTextField
                control={control}
                name="address.pinCode"
                id="address-pincode"
                label={translateText('users.forms.fields.zipCode') || 'Pin Code'}
                errorMessage={errors.address?.pinCode?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={4} data-testid="address-country-field">
              <ControlledTextField
                control={control}
                name="address.country"
                id="address-country"
                label={translateText('users.forms.fields.country') || 'Country'}
                errorMessage={errors.address?.country?.message}
                required
                isGridItem={false}
              />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </StructuredModal>
  );
};

export default AddEditGuestUserView;
